import { Injectable, Signal, computed, signal } from '@angular/core';
import { SortState } from '../models/sort.model';


@Injectable({ providedIn: 'root' })
export class FilterSortService<T extends Record<string, any>> {
    private _data = signal<T[]>([]);
    private _filters = signal<Record<string, any>>({});
    private _sort = signal<SortState>({ key: '', direction: '' });

    filteredData: Signal<T[]> = computed(() => {
        return this.process(this._data(), this._filters(), this._sort());
    });


    connect(config: {
        data: Signal<T[] | undefined>;
        filters: Signal<Record<string, any>>;
        sort: Signal<SortState>;
    }): Signal<T[]> {
        return computed(() => {
            return this.process(
                config.data() ?? [],
                config.filters(),
                config.sort()
            );
        });
    }


    private process(
        data: T[],
        filters: Record<string, any>,
        sort: SortState
    ): T[] {
        let result = [...data];

        // 🔍 FILTERING
        result = result.filter((item) => {
            for (const [key, value] of Object.entries(filters)) {
                if (!value || value === 'Wszystkie') continue;

                // GLOBAL SEARCH
                if (key === 'global') {
                    const search = String(value).toLowerCase();
                    const match = Object.values(item).some((val) =>
                        String(val).toLowerCase().includes(search)
                    );
                    if (!match) return false;
                    continue;
                }

                const itemValue = item[key];

                // BOOLEAN
                if (typeof itemValue === 'boolean') {
                    const boolValue =
                        value === true ||
                        value === 'true' ||
                        value === 'Aktywny';

                    if (itemValue !== boolValue) return false;
                    continue;
                }

                // RANGE (np. price)
                if (typeof value === 'object' && value !== null) {
                    if (value.min != null && itemValue < value.min) return false;
                    if (value.max != null && itemValue > value.max) return false;
                    continue;
                }

                // DEFAULT
                if (itemValue !== value) return false;
            }

            return true;
        });

        // 🔽 SORTING
        if (sort?.key && sort?.direction) {
            const dir = sort.direction.toLowerCase();

            result.sort((a, b) => {
                const aVal = a[sort.key];
                const bVal = b[sort.key];

                if (aVal == null) return 1;
                if (bVal == null) return -1;

                // string
                if (typeof aVal === 'string') {
                    return dir === 'asc'
                        ? aVal.localeCompare(bVal)
                        : bVal.localeCompare(aVal);
                }

                // number / date
                if (aVal > bVal) return dir === 'ASC' ? 1 : -1;
                if (aVal < bVal) return dir === 'ASC' ? -1 : 1;

                return 0;
            });
        }

        return result;
    }
}